import React, { useContext, useState, useCallback, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg"; // Pen icon for editing
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from "../../custom-components/CommonDownload";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { UserStoryFlowDataContext } from "../../main-pages/UserStoryMainPage";
import {
  freeFlowInvest,
  investigate,
  userstoryEvaluation,
} from "../../../services/webServices";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { Filter } from "bad-words";

const filter = new Filter();
const UserStoryComponent = () => {
  const [openDownload, setOpenDownload] = useState(false);
  const {
    userStory: initialUserStory,
    setUserStory,
    evaluationSummary,
  } = useContext(UserStoryFlowDataContext);
  const [userStories, setUserStories] = useState(initialUserStory); // Local state for user story
  const setLoading = useContext(LoadingModalContext);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);
  const [openProfanityDialog, setOpenProfanityDialog] = useState(false);
  const [userStoryText, setUserStoryText] = useState(""); // State for user story text input
  // const { userStory: initialUserStory, setUserStory } = useContext(UserStoryFlowDataContext);

  //const [userStories, setUserStories] = useState(initialUserStory); // Local state for user story
  const defaultUserStoryExpanded =
    Object.keys(initialUserStory).length > 0 && evaluationSummary.length === 0;

  const navigate = useNavigate();
  const location = useLocation();

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  useEffect(() => {
    setUserStories(initialUserStory);
  }, [initialUserStory]);

  const handleCopyClick = () => {
    navigator.clipboard.writeText("Content to be copied");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Handle user story text input and check for profanity in real-time
  const handleUserStoryChange = (event) => {
    const newUserStory = event.target.value;
    setUserStoryText(newUserStory);

    // Check for bad language while typing
    if (filter.isProfane(newUserStory)) {
      setOpenProfanityDialog(true);
    }
  };

  const handleEvaluateUserStory = useCallback(async () => {
    try {
      // Prepare the user story text for evaluation
      const userStoryText =
        typeof userStories === "string"
          ? userStories
          : Object.values(userStories).join(" ");

      // Check if user story contains profanity before evaluation
      if (filter.isProfane(userStoryText)) {
        setOpenProfanityDialog(true); // Show MUI Dialog if profanity is detected
        return; // Stop further evaluation
      }

      setLoading(true);
      let evaluationSummary;

      // Evaluate user story based on type
      if (typeof userStories === "string") {
        const requestData = { data: userStories, parameters: ["Independent", "Negotiable", "Valuable", "Estimable", "Small", "Testable"] };
        evaluationSummary = await userstoryEvaluation(requestData);
        // evaluationSummary = await freeFlowInvest(requestData);
      }

      if (typeof userStories === "object") {
        const requestData = {
          data: `Title: ${userStories.Title}
Description: ${userStories.Description}
Acceptance Criteria: ${userStories["Acceptance Criteria"]}
Additional Information: ${userStories["Additional Information"] || ""}`, parameters: userStories.parameters || ["Independent", "Negotiable", "Valuable", "Estimable", "Small", "Testable"]
        };

        evaluationSummary = await userstoryEvaluation(requestData);
        // evaluationSummary = await investigate(userStories);
      }

      // Navigate to output page
      navigate("/dashboard/userstory/output", {
        state: {
          ...location.state,
          userStory: userStories,
          evaluationSummary: evaluationSummary.data.evaluation,
          isRAGavaiable: evaluationSummary.data.isRAGavaiable
        },
        replace: true,
      });
    } catch (error) {
      console.error("Error evaluating user story:", error);
    } finally {
      setLoading(false);
    }
  }, [userStories, setLoading, navigate, location]);

  const EditableField = ({ label, initialValue, textProps }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValue);
    const [tempValue, setTempValue] = useState(initialValue);

    const handleSave = (label) => {
      setValue(tempValue);
      const updatedUserStory = { ...userStories, [label]: tempValue };

      // Update local state FIRST
      setUserStory(updatedUserStory);

      // THEN update the context (important for other components to see changes)
      setUserStories(updatedUserStory);

      setIsEditing(false);
    };

    const handleCancel = () => {
      setTempValue(value);
      setIsEditing(false);
    };
    return (
      <Box
        sx={{
          "&:hover .edit-icon": { visibility: "visible" },
          // width: "1266px"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            // marginBottom: 1,
            justifyContent: "space-between",
            paddingRight: 3, // Optional: adds spacing from the right edge
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontSize: "14px", marginLeft: 1, fontWeight: "bold", color: "#000" }}
          >
            {label}
          </Typography>
          {!isEditing && (
            <IconButton
              onClick={() => setIsEditing(true)}
              className="edit-icon"
              size="small"
              sx={{ p: 0.5 }}
            >
              <PenIcon style={{ width: 18, height: 18 }} />
            </IconButton>
          )}
        </Box>
        {isEditing ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              name={label}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              sx={{ marginTop: 1, marginRight: 1 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
            <Box>
              {/* Save Button */}
              <Button
                onClick={() => handleSave(label)}
                variant="outline"
                size="small"
                sx={{ marginRight: 1, color: "#49A3FF", textTransform: "none" }}
              >
                Save
              </Button>
              {/* Cancel Button */}
              <Button
                onClick={handleCancel}
                variant="outline"
                size="small"
                sx={{ marginRight: 1, color: "#49A3FF", textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
              mr: 2,
            }}
          >
            <Typography
              variant="body1"
              component="pre"
              sx={{
                fontSize: "14px",
                whiteSpace: "pre-wrap",
                ...textProps?.sx,
              }}
            >
              {value}
            </Typography>

            {/* <IconButton
              onClick={() => setIsEditing(true)}
              className="edit-icon"
              // sx={{ visibility: "hidden" }}
            >
              <PenIcon />
            </IconButton> */}
          </Box>
        )}
      </Box>
    );
  };

  const EditableUserStory = ({ initialUserStory }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [userStoryText, setUserStoryText] = useState(initialUserStory);
    const [tempUserStory, setTempUserStory] = useState(initialUserStory);

    const handleSave = useCallback(() => {
      // useCallback added here
      setUserStoryText(tempUserStory);
      const updatedUserStory = tempUserStory;

      setUserStory(updatedUserStory);
      setUserStories(updatedUserStory);
      setIsEditing(false);
    }, [tempUserStory, setUserStory, setUserStories]); // Dependencies added

    const handleCancel = () => {
      setTempUserStory(userStoryText);
      setIsEditing(false);
    };

    return (
      <Box
        sx={{
          "&:hover .edit-icon": { visibility: "visible" },
          width: "100%",
        }}
      >
        {isEditing ? (
          <Box
            sx={{ display: "flex", alignItems: "center", width: "100%", mb: 1 }}
          >
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={tempUserStory}
              onChange={(e) => setTempUserStory(e.target.value)}
              sx={{ marginTop: 1, marginRight: 1 }}
            />
            <Box>
              <Button
                onClick={handleSave}
                size="small"
                sx={{ color: "#49A3FF" }}
              >
                Save
              </Button>
              <Button
                onClick={handleCancel}
                size="small"
                sx={{ color: "#49A3FF" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start", // align items at the top
              justifyContent: "space-between",
              width: "100%",
              position: "relative",
              mb: 3,
            }}
          >
            <Typography variant="body1" sx={{ flex: 1 }}>
              {userStoryText}
            </Typography>
            <IconButton
              onClick={() => setIsEditing(true)}
              className="edit-icon"
              size="small"
              sx={{
                p: 0.5,
                alignSelf: "flex-start", // 👈 ensures icon stays at top
              }}
            >
              <PenIcon style={{ width: 18, height: 18, marginRight: 9 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  const accordionContent = (
  <Box sx={{ minHeight: "10vh", maxHeight: "50vh", overflowY: "auto" }}>
    {typeof userStories === "string" ? (
      <Typography sx={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
        {userStories}
      </Typography>
    ) : userStories !== null ? (
      <>
        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "14px" }}>
            Title
          </Typography>
          <Typography sx={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
            {userStories["Title"]}
          </Typography>
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "14px" }}>
            Description
          </Typography>
          <Typography sx={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
            {userStories["Description"]}
          </Typography>
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "14px" }}>
            Acceptance Criteria
          </Typography>
          <Typography sx={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
            {userStories["Acceptance Criteria"]}
          </Typography>
        </Box>

        {userStories["Additional Information"] && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "14px" }}>
              Additional Information
            </Typography>
            <Typography sx={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
              {userStories["Additional Information"]}
            </Typography>
          </Box>
        )}
      </>
    ) : (
      <Typography>{userStories}</Typography>
    )}
  </Box>
);

  const handleDownload = () => {
    setOpenDownload(true);
  };

  const buttonsgroup = [
    {
      label: "Download",
      variant: "text",
      startIcon: <FileDownloadOutlinedIcon />,
      onClick: handleDownload, // Call handleDownload function
    },
    {
      label: "Evaluate",
      variant: "contained",
      onClick: handleEvaluateUserStory,
      // startIcon: <GenerateButton />,
      sx: { float: "right" },
    },
  ];

  return (
    <Box >
      <CommonAccordion
        summary={
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            User Story
          </Typography>
        }
        details={accordionContent}
        buttons={buttonsgroup}
        type={"output"}
        fullScreenMode={true}
        defaultExpanded={defaultUserStoryExpanded}
      />
      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceResponse={[
          {
            heading: "User Story",
            type: typeof userStories === "string" ? "text" : "subParagraphs",
            data: userStories,
          },
        ]}
        serviceName="User Story"
        wordData={[
          {
            heading: "User Story",
            type: typeof userStories === "string" ? "text" : "subParagraphs",
            data: userStories, // Use the updated userStory state
          },
        ]}
        documents={["docx", "pdf"]}
      />
      <Dialog
        open={openProfanityDialog}
        onClose={() => setOpenProfanityDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            minWidth: "400px",
            padding: "20px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "20px",
            paddingBottom: "10px",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Inappropriate Input Detected
        </DialogTitle>

        <DialogContent sx={{ paddingBottom: "20px" }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "16px",
              fontFamily: "Roboto, sans-serif",
              color: "#333",
            }}
          >
            This content may violate our usage policies. Rephrase and submit
            again.
          </Typography>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{ justifyContent: "flex-end", padding: "10px 24px" }}
        >
          <Button
            onClick={() => setOpenProfanityDialog(false)}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "14px",
              fontFamily: "Roboto, sans-serif",
              color: "white",
              backgroundColor: "#49A3FF",
              display: "flex",
              alignItems: "center",
              padding: "5px 15px",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserStoryComponent;
